/*
 * Copyright 2026 Sony Corporation
 */
import type { Device, Version, VersionDownloadKind } from '@ips-jit/schema';
import { Alert, Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { useAuth } from '../contexts/auth-context';
import { formatDuration, formatTokens, startDownload } from '../services/download';

import { PackageIcon } from './icons';

const describe = (error: unknown, fallback: string): string => (error instanceof Error ? error.message : fallback);

const statusColor = (v: Version) =>
  v.status === 'build_failed' ? 'error' : v.status === 'built' ? 'success' : 'default';

const deploymentColor = (state: NonNullable<Version['deployment_state']>) =>
  state === 'applied' ? 'success' : state === 'failed' || state === 'rolled_back' ? 'error' : 'default';

const Fact = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Typography variant="caption" color="text.secondary" component="div">
    <Box component="span" sx={{ color: 'text.disabled', mr: 0.75 }}>
      {label}
    </Box>
    {children}
  </Typography>
);

/** A build is called by its version once it has one; until then, and when it failed, by its build number. */
export const titleOf = (v: Version): string => (v.version === null ? `Build ${v.build}` : `Version ${v.version}`);

export interface GeneratedAppsProps {
  projectId: string;
  versions: Version[];
  devices: Device[];
  onDeployAgain: (build: number) => void;
}

/** One line per build in the chooser: its name and where it got to. */
const optionLabel = (v: Version): string =>
  [titleOf(v), (v.deployment_state ?? v.status).replace('_', ' ')].join(' · ');

/**
 * The App section of the project panel: a chooser of the app's builds and the one chosen, with where it has got to
 * and its downloads. It shows the newest build until another is chosen, and goes back to the newest whenever a new
 * build appears.
 */
export const GeneratedApps = ({ projectId, versions, devices, onDeployAgain }: GeneratedAppsProps) => {
  const { api } = useAuth();
  const [chosen, setChosen] = useState<number>();
  const [logOpen, setLogOpen] = useState(false);
  const [preparing, setPreparing] = useState<string>();
  const [error, setError] = useState<string>();

  const newest = versions[0]?.build;
  useEffect(() => {
    setChosen(undefined);
  }, [newest]);

  const v = versions.find((b) => b.build === chosen) ?? versions[0];
  useEffect(() => {
    setLogOpen(false);
  }, [v?.build]);

  const download = async (build: number, kind: VersionDownloadKind) => {
    setPreparing(`${build}-${kind}`);
    setError(undefined);
    try {
      const link = await api.getVersionDownload(projectId, build, kind);
      startDownload(link.url, link.filename);
    } catch (caught) {
      setError(describe(caught, 'Could not prepare the download.'));
    } finally {
      setPreparing(undefined);
    }
  };

  return (
    <Box component="section" aria-label="App">
      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">
          App
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}
        {!v ? (
          <Typography color="text.secondary">No builds yet.</Typography>
        ) : (
          <>
            <TextField
              select
              size="small"
              label="Version"
              value={v.build}
              onChange={(event) => setChosen(Number(event.target.value))}
              // The browser's own list, as for the runtime: the builds are polled, and a redraw must not close it.
              slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
              helperText={v.build === newest ? 'The newest build is shown as it arrives.' : undefined}
            >
              {versions.map((b) => (
                <option key={b.build} value={b.build}>
                  {optionLabel(b)}
                </option>
              ))}
            </TextField>
            <Box
              aria-label={titleOf(v)}
              role="group"
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
            >
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}
                >
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <PackageIcon
                      sx={{ fontSize: 20, color: v.status === 'built' ? 'primary.main' : 'text.disabled' }}
                    />
                    <Typography sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{titleOf(v)}</Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', rowGap: 0.5 }}
                  >
                    <Chip size="small" label={v.status.replace('_', ' ')} color={statusColor(v)} />
                    {v.deployment_state && (
                      <Chip
                        size="small"
                        label={v.deployment_state.replace('_', ' ')}
                        color={deploymentColor(v.deployment_state)}
                      />
                    )}
                  </Stack>
                </Stack>
                {v.summary && <Typography variant="body2">{v.summary}</Typography>}
                {v.error && (
                  <Typography variant="body2" color="error.light">
                    {v.error}
                  </Typography>
                )}
                <Box>
                  <Fact label="Device">{devices.find((d) => d.device_id === v.device_id)?.name ?? 'unknown'}</Fact>
                  <Fact label="Created">{new Date(v.created_at).toLocaleString()}</Fact>
                  {v.artifact_size ? <Fact label="Size">{`${(v.artifact_size / 1e6).toFixed(1)} MB`}</Fact> : null}
                  {v.elapsed_ms !== null && <Fact label="Time">{formatDuration(v.elapsed_ms)}</Fact>}
                  {v.input_tokens !== null && v.output_tokens !== null && (
                    <Fact label="Tokens">{`${formatTokens(v.input_tokens)} in / ${formatTokens(v.output_tokens)} out`}</Fact>
                  )}
                </Box>
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={v.status !== 'built' || preparing !== undefined}
                    onClick={() => void download(v.build, 'binary')}
                  >
                    {preparing === `${v.build}-binary` ? 'Preparing...' : 'Binary (.zip)'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={preparing !== undefined}
                    onClick={() => void download(v.build, 'source')}
                  >
                    {preparing === `${v.build}-source` ? 'Preparing...' : 'Source (.zip)'}
                  </Button>
                  {v.status === 'built' && (
                    <Button size="small" onClick={() => onDeployAgain(v.build)}>
                      Deploy again
                    </Button>
                  )}
                  {v.log_tail && (
                    <Button size="small" onClick={() => setLogOpen(!logOpen)}>
                      {logOpen ? 'Hide log' : 'Build log'}
                    </Button>
                  )}
                </Stack>
                {v.log_tail && logOpen && (
                  <Typography
                    component="pre"
                    variant="caption"
                    sx={{
                      m: 0,
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      overflowWrap: 'anywhere',
                      maxHeight: 320,
                      overflowY: 'auto',
                    }}
                  >
                    {v.log_tail}
                  </Typography>
                )}
              </Stack>
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
};
